import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { faqApi } from "./adminApi";
import { htmlToLines } from "../utils/richText";
import "./Admin.css";
function preview(html) {
  const text = htmlToLines(html)
    .map((line) => line.replace(/<[^>]*>/g, ""))
    .join(" ")
    .trim();
  return text.length > 110 ? `${text.slice(0, 110)}…` : text;
}
export default function ManageFaqs() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [reordering, setReordering] = useState(false);
  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await faqApi.listAll();
      setFaqs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function handleDelete(faq) {
    if (!window.confirm(`Delete this FAQ?\n\n"${faq.question}"\n\nThis can't be undone.`)) return;
    setDeletingId(faq._id);
    try {
      await faqApi.remove(faq._id);
      setFaqs((prev) => prev.filter((f) => f._id !== faq._id));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  }
  async function move(index, direction) {
    const target = index + direction;
    if (target < 0 || target >= faqs.length || reordering) return;
    const next = [...faqs];
    [next[index], next[target]] = [next[target], next[index]];
    const previous = faqs;
    setFaqs(next.map((f, i) => ({ ...f, order: i })));
    setReordering(true);
    setError("");
    try {
      await faqApi.reorder(next.map((f, i) => ({ id: f._id, order: i })));
    } catch (err) {
      setFaqs(previous);
      setError(err.message);
    } finally {
      setReordering(false);
    }
  }
  return (
    <div>
      <div className="admin-topbar">
        <h1>FAQs</h1>
        <Link to="/admin/faqs/new" className="admin-btn admin-btn--primary" style={{ width: "auto" }}>
          <Plus size={16} /> Add FAQ
        </Link>
      </div>
      {error && <div className="admin-error">{error}</div>}
      {loading ? (
        <p>Loading…</p>
      ) : faqs.length === 0 ? (
        <div className="admin-table-wrap">
          <div className="admin-empty">
            No FAQs yet. Click "Add FAQ" to create the first one. (The website shows its built-in FAQs
            until you add some here.)
          </div>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 84 }}>Order</th>
                <th>Question</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {faqs.map((faq, i) => (
                <tr key={faq._id}>
                  <td>
                    <div className="admin-order-btns">
                      <button
                        type="button"
                        className="admin-icon-btn"
                        onClick={() => move(i, -1)}
                        disabled={i === 0 || reordering}
                        aria-label="Move up"
                        title="Move up"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        className="admin-icon-btn"
                        onClick={() => move(i, 1)}
                        disabled={i === faqs.length - 1 || reordering}
                        aria-label="Move down"
                        title="Move down"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                  </td>
                  <td>
                    <strong>{faq.question}</strong>
                    <div style={{ color: "var(--c-mut)", fontSize: "0.82rem", marginTop: 2 }}>
                      {preview(faq.answer)}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-badge admin-badge--${faq.isPublished ? "published" : "draft"}`}>
                      {faq.isPublished ? "Published" : "Draft"}
                    </span>
                  </td>
                  <td>
                    <div className="admin-table__actions">
                      <Link to={`/admin/faqs/${faq._id}/edit`} className="admin-btn admin-btn--outline admin-btn--sm">
                        <Pencil size={14} /> Edit
                      </Link>
                      <button
                        className="admin-btn admin-btn--danger admin-btn--sm"
                        onClick={() => handleDelete(faq)}
                        disabled={deletingId === faq._id}
                      >
                        <Trash2 size={14} /> {deletingId === faq._id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}